import { useToast } from "@design";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router";
import { errorMessage, startConnectorOauthMutation } from "@/api";
import { rememberSignInResult } from "./signInResult";

/**
 * Sign in to an OAuth connector's server as me (phase 6): the backend gives the provider's
 * page, and the provider brings me back to where I started with `?connector_signin=...`.
 */
export function useConnectorSignIn() {
  const { t } = useTranslation("connectors");
  const toast = useToast();
  const location = useLocation();
  const start = useMutation(startConnectorOauthMutation());
  // Coming Back from the provider can restore this page as it was (back-forward cache), still
  // "leaving": reset so the buttons work again.
  const { reset } = start;
  useEffect(() => {
    const onShow = (event: PageTransitionEvent) => {
      if (event.persisted) reset();
    };
    globalThis.addEventListener("pageshow", onShow);
    return () => globalThis.removeEventListener("pageshow", onShow);
  }, [reset]);

  const signIn = (connectorUuid: string, returnTo?: string) =>
    start.mutate(
      {
        path: { connector_uuid: connectorUuid },
        body: { redirectAfter: returnTo ?? `${location.pathname}${location.search}` },
      },
      {
        onSuccess: (response) => window.location.assign(response.data.authorizeUrl),
        onError: (error) => toast.error(errorMessage(error, t("signIn.startFailed"))),
      },
    );

  return {
    signIn,
    // Stays busy while the browser leaves for the provider.
    pendingUuid: start.isPending || start.isSuccess ? start.variables?.path.connector_uuid : undefined,
  };
}

/** Says how a sign-in went when the provider sends me back, then tidies the address. */
export function ConnectorSignInResult() {
  const { t } = useTranslation("connectors");
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const result = params.get("connector_signin");
  const handled = useRef<string | null>(null);

  useEffect(() => {
    if (!result || handled.current === result) return;
    handled.current = result;
    // For whatever started it (e.g. a chat resending its message), independent of render order.
    rememberSignInResult(result);
    if (result === "success") toast.success(t("signIn.succeeded"));
    else toast.error(t("signIn.failed"));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("connector_signin");
        return next;
      },
      { replace: true },
    );
  }, [result, setParams, t, toast]);

  return null;
}
